import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Spinner } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { logger } from "@/telemetry";

/**
 * OAuth callback page for integrations.
 * Handles the redirect from OAuth providers and displays success/error status.
 */
export const IntegrationCallback: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const success = searchParams.get("success") === "true";
  const integration = searchParams.get("integration");
  const email = searchParams.get("email");
  const error = searchParams.get("error");

  if (error)
    logger.warn("OAuth callback error param received", { integration: integration ?? undefined });

  useEffect(() => {
    // Check if running in a popup window
    if (window.opener && !window.opener.closed) {
      // Send result to parent window
      window.opener.postMessage(
        {
          type: "google-oauth-callback",
          success,
          integration,
          email,
          error,
        },
        window.location.origin,
      );
      // Close popup after a brief moment to show status
      const timer = setTimeout(() => {
        window.close();
      }, 1500);

      return () => clearTimeout(timer);
    } else {
      // Redirect to settings page after a short delay (fallback for non-popup)
      const timer = setTimeout(() => {
        navigate("/settings", { replace: true });
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [navigate, success, integration, email, error]);

  const getIntegrationName = (type: string | null): string => {
    switch (type) {
      case "google_drive":
        return "Google Drive";
      case "google_calendar":
        return "Google Calendar";
      case "jira":
        return "Atlassian";
      case "github":
        return "GitHub";
      default:
        return type || "Integration";
    }
  };

  const getIntegrationIcon = (type: string | null): string => {
    switch (type) {
      case "google_drive":
        return "logos:google-drive";
      case "google_calendar":
        return "logos:google-calendar";
      case "jira":
        return "logos:atlassian";
      case "github":
        return "logos:github-icon";
      default:
        return "solar:link-bold";
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-4">
      <div className="text-center space-y-4">
        {success ? (
          <>
            <div className="flex items-center justify-center gap-3">
              <Icon className="w-12 h-12 text-success" icon="solar:check-circle-bold" />
              <Icon className="w-10 h-10" icon={getIntegrationIcon(integration)} />
            </div>
            <h1 className="text-2xl font-semibold text-success">{t("Successfully connected!")}</h1>
            <p className="text-gray-500 dark:text-gray-400">
              {getIntegrationName(integration)} {t("has been connected to your account.")}
            </p>
            {email && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("Connected as")}: {email}
              </p>
            )}
          </>
        ) : (
          <>
            <Icon className="w-12 h-12 text-danger" icon="solar:close-circle-bold" />
            <h1 className="text-2xl font-semibold text-danger">{t("Connection failed")}</h1>
            <p className="text-gray-500 dark:text-gray-400">
              {t("Failed to connect")} {getIntegrationName(integration)}.
            </p>
          </>
        )}

        <div className="flex items-center justify-center gap-2 mt-6 text-sm text-gray-500">
          <Spinner size="sm" />
          <span>{t("Redirecting to settings...")}</span>
        </div>
      </div>
    </div>
  );
};
